/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import usePluginUtils from "./usePluginUtils";
import { useToast } from "../components/ui/Toast";
import { TOAST_ERROR_MESSAGE } from "../constants/constants";
import { useTimeSlotSelection } from "./useTimeSlotSelection";
import { useBooking } from "./useBooking";
import { setPendingAccessService } from "../store/slices/bookingSlice";

const useServiceSelection = (category) => {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const {
    selectedServices,
    setSelectedServices,
    selectedCategories,
    setSelectedCategories,
    setSelectedProducts,
    selectedDate,
    selectedTime,
  } = useBooking();
  const { isPluginEnabled } = usePluginUtils();
  const { validateTimeSlot } = useTimeSlotSelection();
  const groupBookingEnabled = isPluginEnabled("group_booking");
  const membershipPluginEnabled = isPluginEnabled("membership");

  const showErrorToast = useCallback(
    (errorMessage) => {
      toast({
        title: "Error!",
        description: errorMessage,
        variant: "error",
      });
    },
    [toast]
  );

  const validateServiceSelection = useCallback(
    (isSelected) => {
      const uncombinedIsSelected = selectedServices.some((s) => !s.can_combine);

      if (uncombinedIsSelected && !isSelected) {
        return TOAST_ERROR_MESSAGE.UNCOMBINABLE_SERVICE_IS_SELECTED;
      }

      if (!isPluginEnabled("multiple_booking") && selectedServices.length > 0 && !isSelected) {
        return TOAST_ERROR_MESSAGE.MULTIPLE_BOOKING;
      }

      return null;
    },
    [selectedServices, isPluginEnabled]
  );

  const updateServiceBooking = useCallback(
    async (service, counter) => {
      const updatedService =
        groupBookingEnabled && !service.recurring_settings
          ? {
              ...service,
              group_booking_count: counter > 1 ? counter : 1,
              can_combine: counter <= 1,
            }
          : { ...service };

      const updatedServices = selectedServices.length
        ? selectedServices.map((s) => (s.id === service.id ? updatedService : s))
        : [updatedService];

      setSelectedServices(updatedServices);
      setSelectedProducts([]);

      if (selectedTime && selectedDate && updatedServices.length > 0) {
        const isValid = await validateTimeSlot(selectedDate, selectedTime, updatedServices);

        if (!isValid) {
          return;
        }
      }
    },
    [selectedServices, setSelectedServices]
  );

  /**
   * Toggle a service without the membership check.
   * Only MembershipServiceAccessModals should call this, and only after
   * access is GRANTED. UI clicks must go through requestServiceSelection.
   */
  const applyServiceSelection = useCallback(
    async (service, isSelected) => {
      const errorMessage = validateServiceSelection(isSelected);

      if (errorMessage) {
        showErrorToast(errorMessage);

        return;
      }
      const updatedService =
        groupBookingEnabled && !service.recurring_settings
          ? {
              ...service,
              group_booking_count: service.min_group_booking > 1 ? service.min_group_booking : 1,
              can_combine: service.min_group_booking === 1 && service.can_combine !== false,
            }
          : { ...service };

      const updatedServices = isSelected
        ? selectedServices.filter((s) => s.id !== service.id)
        : [...selectedServices, updatedService];

      const updatedCategories = category
        ? isSelected
          ? selectedCategories.filter((c) => c.id !== category.id)
          : [...selectedCategories, category]
        : selectedCategories;

      setSelectedServices(updatedServices);
      setSelectedProducts([]);

      if (category) {
        setSelectedCategories(updatedCategories);
      }

      if (selectedTime && selectedDate && updatedServices.length > 0) {
        const isValid = await validateTimeSlot(selectedDate, selectedTime, updatedServices, updatedCategories);

        if (!isValid) {
          return;
        }
      }
    },
    [
      selectedServices,
      setSelectedServices,
      selectedCategories,
      setSelectedCategories,
      category,
      validateServiceSelection,
      showErrorToast,
      selectedDate,
      selectedTime,
      validateTimeSlot,
      groupBookingEnabled,
      setSelectedProducts,
    ]
  );

  /**
   * Selection from a UI click. For members-only services, sets
   * pendingAccessService and returns — MembershipServiceAccessModals takes
   * over and calls applyServiceSelection once access is GRANTED.
   */
  const requestServiceSelection = useCallback(
    async (service, isSelected) => {
      const requiresMembership =
        membershipPluginEnabled && Array.isArray(service?.memberships) && service.memberships.length > 0;

      if (!isSelected && requiresMembership) {
        dispatch(setPendingAccessService(service));
        return;
      }

      await applyServiceSelection(service, isSelected);
    },
    [membershipPluginEnabled, dispatch, applyServiceSelection]
  );

  return { requestServiceSelection, applyServiceSelection, updateServiceBooking };
};

export default useServiceSelection;
